import Vue from 'vue';

const defaultState = () => {
  return {
    ajax: null,
    currentUser: null,
    errorResponse: null,
    hasRated: false,
    lastActive: null,
    onTour: false,
    showMobileMenu: false,
    tourScheduled: false,
    tourSequence: null,
    tourType: '',
    trackingAttributes: ['sc', 'partner_ref'],
  };
};

const state = defaultState();

const getters = {
  ajax: state => state.ajax,
  classification: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/get'];
    if (user && user.classificationId) {
      return rootGetters['classifications/get'][user.classificationId];
    }
  },
  userClass: (state, getters, rootState, rootGetters) => {
    const user = rootGetters['user/get'];
    return user?.classification;
  },
  // This getter is considered deprecated in favor of the user/get getter
  // It will remain intact until the rest of the code has a change to migrate to the new module
  currentUser: (state, getters, rootState, rootGetters) => rootGetters['user/get'],
  authenticated: (state, getters, rootState, rootGetters) => rootGetters['user/get'],
  inactive: state => state.lastActive && (Date.now() - state.lastActive) > 86400000, // 1 day in millis
  isSubscribed: (state, getters, rootState, rootGetters) => {
    if (getters.currentUser) {
      return rootGetters['purchaseOrders/subscription'].some(po => getters.currentUser.purchaseOrders.includes(po.id));
    }
  },
  hasPurchasedTiered: (state, getters, rootState, rootGetters) => {
    const purchaseReceipts = rootGetters['purchaseReceipts/all'];
    const tieredPricingPurchaseOrderIds = rootGetters['purchaseOrders/tieredPricing'].map(po => po.id);

    return purchaseReceipts.find(pr => !pr.trialMode && tieredPricingPurchaseOrderIds.includes(pr.purchaseOrderId));
  },
  hasPurchasedSimulation: (state, getters, rootState, rootGetters) => {
    if (getters.currentUser) {
      return rootGetters['purchaseOrders/simulation'].some(po => getters.currentUser.purchaseOrders.includes(po.id));
    }
  },
  hasCompletedTrial: (state, getters, rootState, rootGetters) => {
    return rootGetters['purchaseReceipts/all'].find(pr => pr.isSubscriptionTrial);
  },
  hasPaid: (state, getters) => !!(getters.isSubscribed || getters.hasPurchasedTiered),
  showMobileMenu: state => state.showMobileMenu,
  hasRated: state => state.hasRated,
  onTour: state => state.onTour,
  tourScheduled: state => state.tourScheduled,
  tourSequence: state => state.tourSequence,
  tourType: state => state.tourType,
  trackingAttributes: state => state.trackingAttributes,
  errorResponse: state => state.errorResponse,
};

const mutations = {
  configureAjax(state, instance) {
    state.ajax = instance;
  },
  lastActive(state) {
    state.lastActive = Date.now();
  },
  toggleMobileMenu(state, show) {
    state.showMobileMenu = show;
  },
  toggleHasRated(state) {
    state.hasRated = !state.hasRated;
  },
  updateCurrentUser(state, params = {}) {
    state.currentUser = {...state.currentUser, ...params};
  },
  reset(state) {
    Object.assign(state, defaultState());
  },
  scheduleTour(state, bool) {
    state.tourScheduled = bool;
  },
  setOnTour(state, bool) {
    state.onTour = bool;
  },
  setTourSequence(state, seq) {
    state.tourSequence = seq;
  },
  setTourType(state, type) {
    state.tourType = type;
  },
  setErrorResponse(state, errorResponse) {
    state.errorResponse = errorResponse;
  },
};

const configureUser = (context, commit, rootGetters, user, signUp = false) => {
  commit('user/set', user, { root: true });
  commit('purchaseReceipts/set', user.purchaseReceipts, { root: true });
  commit('categories/setCategories', user.categories, { root: true });
  commit('userQuizzes/set', user.userQuizzes, { root: true });
  commit('questionnaireUserStates/set', user.questionnaireUserStates, { root: true });
  commit('checklistStates/set', user.checklistStates, { root: true });
  commit('userCertifications/setUserCertifications', user.userCertifications, { root: true });
  commit('navigationItems/setDynamicNavigationItems', rootGetters, { root: true });
  let disabledDashboards = user?.data?.disabledDashboards;
  if (disabledDashboards) {
    try {
      disabledDashboards = JSON.parse(disabledDashboards);
      commit('ui/setDashboardState', disabledDashboards, { root: true });
    } catch {};
  };
  const streakStartDate = user?.data?.streakStartDate;
  if (streakStartDate) {
    commit('streak/setStartDate', streakStartDate, { root: true });
  }

  context._vm.$analytics.config(rootGetters);

  let freePo = rootGetters['purchaseOrders/all'].find(po => po.purchaseOrderType === 'Tiered Pricing' && po.isFree);
  let tieredPo = rootGetters['purchaseOrders/tieredPricing'].find(po => user.purchaseOrders.includes(po.id)) || freePo;
  let subscriptionPo = rootGetters['purchaseOrders/subscription'].find(po => user.purchaseOrders.includes(po.id)) || freePo;
  context._vm.$apptimize.config({ ...rootGetters['session/currentUser'], productTier: tieredPo, subscriptionPo: subscriptionPo }, signUp);
};

const actions = {
  signIn({ dispatch, commit, getters, rootGetters }, [params, loadPromise]) {
    // Within the sign up flow inside the app, we will dispatch the sign up action directly.
    // However, if signing in from an external site, this action will be dispatched from the router.
    // In order to register this user's registration time correctly in apptimize, we need to check
    // if they are coming from an external site by checking for the `signup` property. We can pass
    // the result of that check to `configureUser` which will go on to the apptimize config and register
    // the users sign up time correctly. This time can be used as a filtering criteria for a-b tests.
    const hasSignUpParams = params.hasOwnProperty('signup');
    return rootGetters['session/ajax'].post('/sessions', params).then(response => {
      if (response?.data?.mergeBanner) {
        commit('ui/setMergeBanner', true, { root: true });
      }
      if (loadPromise) {
        const context = this;
        (async function() {
          await loadPromise.then((res) => {
            configureUser(context, commit, rootGetters, response.data.user, hasSignUpParams);
          });
        })();
      } else {
        configureUser(this, commit, rootGetters, response.data.user, hasSignUpParams);
      }

      return response;
    }).catch(error => {
      // Ensure we're signed out, regardless of error
      commit('user/set', null, { root: true });
      return Promise.reject(error);
    });
  },
  signUp({ dispatch, commit, getters, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/registrations', params).then(response => {
      configureUser(this, commit, rootGetters, response.data.user, true);
      return response.data.activationResults;
    }).catch(error => {
      // Ensure we're signed out, regardless of error
      commit('user/set', null, { root: true });
      return Promise.reject(error);
    });
  },
  signOut({ dispatch, commit, getters, rootGetters }) {
    dispatch('deleteTrackingCookies');
    commit('user/set', null, { root: true });
    return rootGetters['session/ajax'].delete('/sessions').finally(() => {
      window.location.href = '/';
    });
  },
  resetPassword({ dispatch, commit, getters, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/passwords/', params);
  },
  resetProgress({ dispatch, commit, getters, rootGetters }) {
    return rootGetters['session/ajax'].post('user/reset').then(response => {
      commit('categoryStates/reset', null, { root: true });
      commit('flashcardStates/reset', null, { root: true });
      commit('mnemonicStates/reset', null, { root: true });
    });
  },
  loadUser({ dispatch, commit, getters, rootGetters }, loadPromise) {
    return rootGetters['session/ajax'].get('/user').then(response => {
      const context = this;
      if (loadPromise) {
        (async function() {
          await loadPromise.then((res) => {
            configureUser(context, commit, rootGetters, response.data.user);
          });
        })();
      } else {
        configureUser(this, commit, rootGetters, response.data.user);
      }
      commit('setErrorResponse', null);
      return response;
    }).catch(error => {
      commit('setErrorResponse', error.response.data);
      // Ensure we're signed out, regardless of error
      commit('user/set', null, { root: true });
      return Promise.reject(error);
    });
  },
  updateUser({ dispatch, commit, getters, rootGetters }, params) {
    return dispatch('user/update', params, { root: true });
  },
  updateUserProfile({ dispatch, commit, getters, rootGetters }, params) {
    return rootGetters['session/ajax'].put('/user_profile', params).then(response => {
      commit('user/set', response.data.user, { root: true });
      this._vm.$analytics.config(rootGetters);

      return response;
    });
  },
  sendSupportMessage({ getters, rootGetters }, params) {
    return rootGetters['session/ajax'].post('/support', {supportForm: params});
  },
  getEula({ rootGetters }) {
    return rootGetters['session/ajax'].get('/eula');
  },
  deleteTrackingCookies({getters}) {
    getters.trackingAttributes.map(item => {
      this._vm.$cookie.delete(item);
    });
  },
  sendReport({ dispatch, commit, getters, rootGetters }, params) {
    return rootGetters['session/ajax'].post('reports', params);
  },
  // this is only used by tests but is important!
  // silences annoying console warnings, not sure how else to do it
  setupTest() {
    Vue.config.productionTip = false;
    Vue.config.devtools = false;
  },
  incrementTourSequence({ dispatch, commit, getters, rootGetters }) {
    const curr = getters['tourSequence'];
    commit('setTourSequence', curr + 1);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
